import { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { map } from "lodash/fp";

import useInviteToWorkspace from "@app/analytics/hooks/workspace/useInviteToWorkspace";
import type { WorkspaceUserInviteBody } from "@app/common/account/api";
import apiClient from "@app/common/api/apiClient";
import { isResponseErrorWithStatus } from "@app/common/utils";
import type {
  EnterpriseTeam,
  EnterpriseWorkspace,
  EnterpriseWorkspaceUser,
} from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { isPromiseFulfilled } from "@app/utils/utils";

interface AddOrInvitesUsersToTeamBody {
  workspaceId: EnterpriseWorkspace["id"];
  invites: WorkspaceUserInviteBody[];
  users: EnterpriseWorkspaceUser[];
}

interface AddOrInviteUsersResponse {
  failures: {
    emails: string[];
    users: EnterpriseWorkspaceUser[];
  };
  /* Total number of invites sent; includes invite to workspace as well as "invite" to team */
  numInvitesSent: number;
  errorCodes: number[];
}

interface UseWorkspaceTeam {
  loading: boolean;
  team: EnterpriseTeam;
  error: boolean;
  addUsers: (
    body: AddOrInvitesUsersToTeamBody
  ) => Promise<AddOrInviteUsersResponse>;
  addingUsers: boolean;
}

const DEFAULT_ACCESS_EDIT_SETTINGS = {
  view: false,
  edit: false,
};

const DEFAULT_TEAM: EnterpriseTeam = {
  id: "",
  workspaceId: "",
  name: "",
  users: [],
  settings: {
    shareTeamData: DEFAULT_ACCESS_EDIT_SETTINGS,
    storeRecording: DEFAULT_ACCESS_EDIT_SETTINGS,
    teamRollups: DEFAULT_ACCESS_EDIT_SETTINGS,
    transcripts: DEFAULT_ACCESS_EDIT_SETTINGS,
    workspaceRollups: DEFAULT_ACCESS_EDIT_SETTINGS,
  },
};

const useWorkspaceTeam = (teamId?: string): UseWorkspaceTeam => {
  const [addingUsers, setAddingUsers] = useState(false);

  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspace();
  const inviteToWorkspace = useInviteToWorkspace();

  const teamByIdQuery = useQuery({
    queryKey: ["workspace", "team", teamId],
    queryFn: () => apiClient.workspace.teams.getTeam(workspaceId!, teamId!),
    enabled: Boolean(workspaceId) && Boolean(teamId),
    refetchOnWindowFocus: false,
  });

  const addUsersToTeamMutation = useMutation({
    mutationFn: apiClient.workspace.teams.addUsersToTeam,
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["workspace"] }),
  });

  const addUsersToTeam = async ({
    workspaceId,
    invites,
    users,
  }: AddOrInvitesUsersToTeamBody) => {
    const response: AddOrInviteUsersResponse = {
      failures: {
        emails: [],
        users: [],
      },
      numInvitesSent: 0,
      errorCodes: [],
    };

    if (!teamId) return response;

    setAddingUsers(true);

    const [inviteResponse, addUsersResponse] = await Promise.allSettled([
      inviteToWorkspace.mutateAsync({ workspaceId, invites }),
      addUsersToTeamMutation.mutateAsync({ workspaceId, teamId, users }),
    ]);

    if (isPromiseFulfilled(inviteResponse)) {
      response.numInvitesSent += inviteResponse.value.invites?.length ?? 0;
      response.failures.emails = map("email", inviteResponse.value.failures);
    } else {
      if (isResponseErrorWithStatus(inviteResponse.reason, 402)) {
        response.errorCodes.push(402);
      }
      response.failures.emails = map("email", invites);
    }

    if (isPromiseFulfilled(addUsersResponse)) {
      response.numInvitesSent += users.length - addUsersResponse.value.length;
      response.failures.users = addUsersResponse.value;
    }

    setAddingUsers(false);
    return response;
  };

  return {
    loading: teamByIdQuery.isLoading,
    error: teamByIdQuery.isError,
    team: teamByIdQuery.data ?? DEFAULT_TEAM,
    addUsers: addUsersToTeam,
    addingUsers,
  };
};

export default useWorkspaceTeam;
